import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useMasters } from "../form/engine";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const POSTER_ROLES = ["cafe_owner", "cloud_kitchen", "admin"];
const SLOTS = ["Morning", "Afternoon", "Evening", "Full day"];

// Chef Gigs — hotels/cafes/cloud kitchens post temporary chef work,
// FUNZ Certified chefs accept. Gig flow: open → filled → completed.
export const ChefGigsPanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const masters = useMasters();
  const isPoster = POSTER_ROLES.includes(user.role);
  const isChef = user.role === "chef" || user.role === "admin";
  const [tab, setTab] = useState("open");
  const [gigs, setGigs] = useState(null);
  const [mine, setMine] = useState([]);
  const [posted, setPosted] = useState([]);
  const [cuisine, setCuisine] = useState("");
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", cuisine: "", date: "", slot: "Evening", pay: "", location: "", headcount: 1, description: "" });

  const load = () => {
    axios.get(`${API}/chef-gigs?cuisine=${encodeURIComponent(cuisine)}`, headers()).then((r) => setGigs(r.data.gigs)).catch(() => setGigs([]));
    if (isChef) axios.get(`${API}/chef-gigs/my`, headers()).then((r) => setMine(r.data.gigs)).catch(() => {});
    if (isPoster) axios.get(`${API}/chef-gigs/mine`, headers()).then((r) => setPosted(r.data.gigs)).catch(() => {});
  };
  useEffect(load, [cuisine]);

  const act = async (id, action, body = {}) => {
    setBusy(id);
    try {
      await axios.post(`${API}/chef-gigs/${id}/${action}`, body, headers());
      toast.success(action === "accept" ? "Gig accepted 🎉" : `Gig ${body.status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const post = async () => {
    setBusy("post");
    try {
      await axios.post(`${API}/chef-gigs`, { ...form, pay: Number(form.pay), headcount: Number(form.headcount) || 1 }, headers());
      toast.success("Gig posted — certified chefs can see it now 🧑‍🍳");
      setForm({ title: "", cuisine: "", date: "", slot: "Evening", pay: "", location: "", headcount: 1, description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const tabs = [
    { id: "open", label: `💼 ${t(lang, "gig_open")}` },
    ...(isPoster ? [{ id: "post", label: `➕ ${t(lang, "gig_post")}` }, { id: "posted", label: `📋 ${t(lang, "gig_posted")}` }] : []),
    ...(isChef ? [{ id: "my", label: `🧑‍🍳 ${t(lang, "gig_mine")}` }] : []),
  ];

  const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

  const GigCard = ({ g, mode }) => (
    <div key={g.id} data-testid={`gig-${g.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{g.title}</p>
          <p className="font-jbmono text-[10px] text-slate-500">
            {g.poster_name} · {g.date} · {g.slot}{g.cuisine ? ` · ${g.cuisine}` : ""} · 👥 {g.headcount}
          </p>
        </div>
        <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">₹{g.pay}</span>
      </div>
      {g.description && <p className="mt-1.5 text-xs text-slate-400">{g.description}</p>}
      <p className="mt-1 font-jbmono text-[10px] text-slate-500">📍 {g.location}</p>
      <div className="mt-2.5 flex items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
          g.status === "filled" ? "bg-[#22C55E]/10 text-[#22C55E]" : g.status === "open" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400"}`}>
          {g.status}{g.chef_name && g.status !== "open" ? ` · ${g.chef_name}` : ""}
        </span>
        {mode === "browse" && isChef && (
          <button data-testid={`gig-accept-${g.id.slice(-6)}`} onClick={() => act(g.id, "accept")} disabled={busy === g.id}
            className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-4 py-1.5 text-[10px] font-bold text-[#09090B] disabled:opacity-50">
            {busy === g.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : t(lang, "gig_accept")}
          </button>
        )}
        {mode === "my" && g.status === "filled" && (
          <button data-testid={`gig-withdraw-${g.id.slice(-6)}`} onClick={() => act(g.id, "status", { status: "open" })} disabled={busy === g.id}
            className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">
            Withdraw
          </button>
        )}
        {mode === "posted" && g.status === "filled" && (
          <button data-testid={`gig-complete-${g.id.slice(-6)}`} onClick={() => act(g.id, "status", { status: "completed" })} disabled={busy === g.id}
            className="rounded-full bg-[#06B6D4] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">
            Mark completed
          </button>
        )}
        {mode === "posted" && ["open", "filled"].includes(g.status) && (
          <button data-testid={`gig-cancel-${g.id.slice(-6)}`} onClick={() => act(g.id, "status", { status: "cancelled" })} disabled={busy === g.id}
            className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">
            {t(lang, "cancel")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="panel-chefgigs">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`gig-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#F97316] to-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "open" && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {["", ...(masters.cuisines || []).slice(0, 8)].map((c) => (
              <button key={c || "all"} data-testid={`gig-cuisine-${(c || "all").toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCuisine(c)}
                className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${cuisine === c ? "bg-[#F97316] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                {c || "All cuisines"}
              </button>
            ))}
          </div>
          {gigs === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : gigs.length === 0 ? (
            <p className="text-sm text-slate-500">No open gigs right now{isPoster ? " — post one!" : " — check back soon."}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{gigs.map((g) => <GigCard key={g.id} g={g} mode="browse" />)}</div>
          )}
          {!isChef && !isPoster && (
            <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">Only FUNZ Certified chefs can accept gigs</p>
          )}
        </>
      )}

      {tab === "post" && isPoster && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="gig-create">
          <p className="font-display text-sm font-bold">{t(lang, "gig_post")}</p>
          <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <input data-testid="gig-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekend Banquet Chef" className={inputCls} />
            <div>
              <input data-testid="gig-new-cuisine" value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })} placeholder="Cuisine specialty" list="gig-cuisines" className={inputCls} />
              <datalist id="gig-cuisines">{(masters.cuisines || []).map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <input data-testid="gig-new-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            <input data-testid="gig-new-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (venue, area, city)" className={inputCls} />
            <div className="flex gap-2">
              <input data-testid="gig-new-pay" type="number" value={form.pay} onChange={(e) => setForm({ ...form, pay: e.target.value })} placeholder="Pay ₹" className={inputCls} />
              <input data-testid="gig-new-headcount" type="number" min="1" max="20" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} placeholder="Chefs needed" className={inputCls} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {SLOTS.map((s) => (
                <button key={s} data-testid={`gig-slot-${s.toLowerCase().replace(" ", "-")}`} onClick={() => setForm({ ...form, slot: s })}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${form.slot === s ? "bg-[#F97316] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                  {s}
                </button>
              ))}
            </div>
            <textarea data-testid="gig-new-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What should the chef handle?" rows={2} className={`${inputCls} sm:col-span-2`} />
          </div>
          <button data-testid="gig-create-submit" onClick={post} disabled={busy === "post" || !form.title || !form.date || !form.pay || !form.location}
            className="mt-3 rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
            {busy === "post" ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : t(lang, "gig_post")}
          </button>
        </div>
      )}

      {tab === "posted" && isPoster && (
        <div className="space-y-2.5">
          {posted.length === 0 && <p className="text-sm text-slate-500">No gigs posted yet.</p>}
          {posted.map((g) => <GigCard key={g.id} g={g} mode="posted" />)}
        </div>
      )}

      {tab === "my" && isChef && (
        <div className="space-y-2.5">
          {mine.length === 0 && <p className="text-sm text-slate-500">No accepted gigs yet — grab one from Open gigs.</p>}
          {mine.map((g) => <GigCard key={g.id} g={g} mode="my" />)}
        </div>
      )}
    </div>
  );
};
